// Credential resolver for API calls (contract §2). identity has landed — see
// context/plan/2026-07-14-identity-binding.md. Real login now issues an HttpOnly
// `bo_access_token` cookie (set by /auth/callback), verified server-side by every proxied
// /api/<service>/* call (src/index.ts `resolveClientCode`) — the frontend never sees the
// raw token (HttpOnly, by design), so it cannot attach it as an `Authorization: Bearer`
// header itself. What the frontend DOES need — its own claims, for UI permission gating —
// comes from `whoamiClaims()` below (GET /auth/whoami, server-side cookie -> live
// /api/iam/me), falling back to the dev x-ckn-claims header only when unauthenticated.
//
// `iam` is the tenant `client_code` string (e.g. "MSK") — identity's real claim shape,
// NOT a UUID (a prior version of this file modeled it as one; every ckn.business.* table's
// tenant column is actually keyed by this same client_code string, not a uuid tenant id).
/**
 * Every permission the three services enforce (from the contract). The dev claims grant them all
 * so the UI is not blocked by RBAC while identity is stubbed. Narrow this (or use a claims switcher)
 * to test permission gating.
 */
export const ALL_PERMISSIONS = [
    // customer
    "customer.view", "customer.pii.view", "customer.create", "customer.edit", "customer.delete",
    "customer.merge", "customer.profile.assign", "customer.owner.assign",
    "customer.consent.view", "customer.consent.manage", "customer.consent.export",
    "customer.segment.manage", "segment.activate",
    "customer.case.manage", "case.assign",
    "customer.claim.manage",
    "customer.audience.manage", "audience.export",
    "customer.access.configure", "customer.access.validate", "access.attend",
    "membership.points.credit.request", "membership.points.credit.approve",
    // inventory
    "inventory.balance.view", "inventory.movement.view", "inventory.adjustment.create",
    "inventory.count.create", "inventory.count.recount", "inventory.count.approve",
    "inventory.replenishment.view", "inventory.replenishment.request", "inventory.replenishment.approve",
    "inventory.receive.manage", "inventory.discrepancy.manage", "inventory.putaway.manage",
    "inventory.pick.manage", "inventory.possales.view",
    "inventory.reserve.create", "inventory.reserve.release", "inventory.reserve.deduct",
    "inventory.location.configure", "inventory.reorder.configure", "inventory.export",
    "inventory.return.process", "inventory.returncw.create", "inventory.returncw.approve",
    "inventory.defectbasket.manage",
    "sample.tracking.view", "sample.tracking.manage", "sample.alert.ack",
    "inventory.rfid.manage", "inventory.stockstate.manage",
    // sales
    "oms.so.view", "oms.so.create", "oms.so.edit", "oms.so.confirm",
    "oms.do.view", "oms.do.create", "oms.do.dispatch", "oms.do.confirm",
    "delivery.carrier.manage", "delivery.tracking.view", "delivery.pod.manage",
    "sales.return.view", "sales.return.create", "sales.return.approve", "sales.exchange.create",
    "oms.pnl.view", "analytics.sales.view", "sales.target.manage",
    "sales.receipt.view", "sales.commission.view", "sales.staff.view",
    "sales.tax.view", "sales.tax.issue", "sales.tax.approve", "sales.tax.void", "sales.tax.refund.complete",
    // orders service (contract naming, `orders.*`) — the OMS panes gate their write affordances on these.
    "orders.quotation.view", "orders.quotation.edit", "orders.quotation.approve",
    "orders.sales_order.view", "orders.sales_order.create", "orders.sales_order.edit", "orders.sales_order.confirm", "orders.sales_order.cancel",
    "orders.credit.override",
    "orders.delivery_order.view", "orders.delivery_order.create", "orders.delivery_order.edit",
    "orders.delivery_order.release", "orders.delivery_order.dispatch", "orders.delivery_order.cancel",
    "orders.delivery.view", "orders.delivery.edit",
    "orders.receipt.view", "orders.receipt.sell", "orders.receipt.void",
    "orders.billing.view", "orders.billing.edit", "orders.billing.issue",
    "orders.tax.view", "orders.tax.refund",
    "orders.return.view", "orders.return.edit", "orders.return.approve",
    "orders.rma.view", "orders.rma.edit", "orders.rma.inspect", "orders.rma.approve",
];
// Dev/demo tenant — matches the real seeded tenant (identity's iam.client_code = "MSK",
// same value every ckn.business.* row and the /api proxy's CLIENT_CODE env var use).
// Override at runtime via localStorage `ckn_claims` or window.__CKN_DEV_CLAIMS__.
const DEV_CLAIMS = { iam: "MSK", sub: "u.dev", permissions: ALL_PERMISSIONS };
function readLocalStorage(key) {
    try {
        return globalThis.localStorage?.getItem?.(key) ?? null;
    }
    catch {
        return null;
    }
}
/** Signed out, on a real host: no claims, therefore no affordances. See isLocalDev(). */
const NO_CLAIMS = { iam: "", sub: "", permissions: [] };
function devClaims() {
    const g = globalThis;
    if (g.__CKN_DEV_CLAIMS__)
        return g.__CKN_DEV_CLAIMS__;
    const stored = readLocalStorage("ckn_claims");
    if (stored) {
        try {
            return JSON.parse(stored);
        }
        catch {
            /* fall through */
        }
    }
    // The last resort — nobody is signed in and nobody set an override. DEV_CLAIMS grants
    // ALL_PERMISSIONS, which is right on a developer's machine with identity stopped and wrong
    // on a deployment, where it drew the whole owner console for an anonymous visitor. An
    // explicit `ckn_claims` / `__CKN_DEV_CLAIMS__` override above still works anywhere, because
    // that is somebody deliberately testing a claim set rather than a silent default.
    return isLocalDev() ? DEV_CLAIMS : NO_CLAIMS;
}
/**
 * Is this a developer's machine? The blanket grant below is scoped to one, and to nothing else.
 *
 * `ALL_PERMISSIONS` exists so a developer running brandos with identity stopped is not locked
 * out of every screen. On a real deployment that same fallback is a hole in the other direction:
 * an unauthenticated visitor — or an authenticated one whose /auth/whoami call happened to fail
 * — was shown every affordance in the console, each of which the gateway then rejected. The
 * check is the origin because that is the one thing a browser cannot be talked into lying about
 * by anything served from a real host.
 *
 * This is UX only either way, and it always was: the gateway resolves permissions live from
 * identity on every call (identity's /api/iam/* "do not trust stale claims"). Getting it wrong
 * never granted access, it only ever mis-drew the buttons.
 */
function isLocalDev() {
    try {
        const h = globalThis.location?.hostname ?? "";
        return h === "localhost" || h === "127.0.0.1" || h === "::1" || h === "";
    }
    catch {
        return false; // no location (SSR/test) → assume production and grant nothing
    }
}
/**
 * UI permission gate — true if the current session's claims grant `perm`. Used to hide/disable
 * affordances the caller can't use; the gateway still enforces the real permission server-side, so
 * this is UX only, never a security boundary.
 *
 * Fails **open on localhost** and **closed everywhere else**. It used to fail open everywhere,
 * which meant a whoami hiccup in production drew the full owner UI for whoever was looking.
 */
export function hasPerm(perm) {
    try {
        const perms = devClaims().permissions;
        if (!Array.isArray(perms))
            return isLocalDev();
        return perms.includes(perm) || perms.includes("*");
    }
    catch {
        return isLocalDev();
    }
}
/**
 * Header(s) to attach to every request. Bearer token if present, else — on a developer's
 * machine ONLY — the dev claim set.
 *
 * `x-ckn-claims` must never leave a deployment, and not for tidiness: it is what took the whole
 * admin console down on 2026-08-01. Nothing reads it. No route in src/index.ts parses it, it is
 * absent from FORWARDED_HEADERS so the Hub never sees it, and it is browser-set and therefore
 * forgeable, which is exactly why the /api gate stopped trusting this class of header and moved
 * to the signed `bo_surface` cookie. A deployment authenticates by the HttpOnly bo_access_token
 * cookie, read server-side.
 *
 * What it cost: `__CKN_DEV_CLAIMS__` is filled from /auth/whoami, and whoami returns the FULL
 * unioned permission set — narrowing the TOKEN never narrows it (see the identity-side note on
 * client_permission_service). For a `sysadmin` that is all 351 platform permissions, so the
 * header serialised to ~11 KB on EVERY /api call, past Apache's default LimitRequestFieldSize
 * of 8190. Apache answered 400 "Size of a request header field exceeds server limit" without
 * ever reaching node — no brandos log line, no Hub execution, and a Product List with no rows.
 * The few calls that did land were the ones fired before whoami resolved, while the claim set
 * was still empty.
 *
 * The browser keeps using `devClaims()` for its own UI gating (`can()`); only the wire changes.
 */
export function currentCredential() {
    const g = globalThis;
    const token = g.__CKN_TOKEN__ ?? readLocalStorage("ckn_token");
    if (token)
        return { Authorization: `Bearer ${String(token)}` };
    if (!isLocalDev())
        return {};
    return { "x-ckn-claims": JSON.stringify(devClaims()) };
}
const SIGNED_OUT = {
    authenticated: false, iam: "", iamName: "", role: "", roles: [], sub: "", name: "",
    permissions: [], iams: [], disabledModules: [], disabledPanes: [], isPlatformRoot: false,
    menuAccess: null,
};
let session = SIGNED_OUT;
/** The resolved session. Await `whoamiReady()` first, or this may still be the signed-out one. */
export function currentSession() {
    return session;
}
// Populates window.__CKN_DEV_CLAIMS__ from the real session (GET /auth/whoami — same-origin,
// reads the HttpOnly bo_access_token cookie server-side) so `devClaims()`'s existing
// precedence picks up REAL permissions with no change to it or any of its ~10 callers.
async function refreshWhoami() {
    try {
        const res = await fetch("/auth/whoami");
        if (!res.ok)
            return;
        const body = await res.json();
        if (!body.authenticated || !body.iam || !body.sub)
            return;
        session = {
            authenticated: true,
            iam: body.iam,
            iamName: body.iamName ?? body.iam,
            role: body.role ?? "",
            roles: Array.isArray(body.roles) ? body.roles : (body.role ? [body.role] : []),
            sub: body.sub,
            name: String(body.name ?? body.email ?? ""),
            permissions: body.permissions ?? [],
            iams: body.iams ?? [],
            disabledModules: body.disabledModules ?? [],
            disabledPanes: body.disabledPanes ?? [],
            isPlatformRoot: body.isPlatformRoot === true,
            menuAccess: body.menuAccess ?? null,
        };
        globalThis.__CKN_DEV_CLAIMS__ = { iam: session.iam, sub: session.sub, permissions: session.permissions };
    }
    catch {
        // identity/whoami unreachable — stay signed out, don't block the app.
    }
}
// Kicked off once at module load. `whoamiReady()` hands back that SAME promise rather than
// re-fetching, so every pane can await it and only the first one pays for the round-trip.
//
// Awaiting matters: this used to be pure fire-and-forget, and panes gated their buttons in
// onInitializing() — which reliably ran first. Every pane therefore drew itself from the
// fallback claims and only corrected on a later reload, so the first paint after sign-in showed
// affordances the user did not have. LiveComponent.onInitializing() now awaits this.
let pending = null;
export function whoamiReady() {
    if (!pending)
        pending = refreshWhoami();
    return pending;
}
/** Re-fetch the session (after a workspace switch, or a sign-in in another tab). */
export function reloadWhoami() {
    pending = refreshWhoami();
    return pending;
}
if (typeof globalThis !== "undefined" && typeof globalThis.fetch === "function" && typeof globalThis.window !== "undefined") {
    void whoamiReady();
}
export { refreshWhoami };
//# sourceMappingURL=auth.js.map